import { useEffect, useState } from "react";
import { Download, Share, Plus, MoreVertical, Smartphone, Apple, Check, X } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const Step = ({ n, icon: Icon, children }) => (
  <div className="flex items-start gap-3">
    <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-blue-600 text-white text-xs font-bold">{n}</div>
    <div className="flex items-center gap-2 text-sm text-slate-700 leading-relaxed pt-0.5">
      {Icon && <Icon className="h-4 w-4 text-blue-600 shrink-0" />}
      <span>{children}</span>
    </div>
  </div>
);

export const InstallAppButton = ({ compact = false, className = "" }) => {
  const [installable, setInstallable] = useState(!!window.__wzInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => { setInstalled(true); setInstallable(false); setOpen(false); };
    window.addEventListener("wz-installable", onInstallable);
    window.addEventListener("wz-installed", onInstalled);
    return () => {
      window.removeEventListener("wz-installable", onInstallable);
      window.removeEventListener("wz-installed", onInstalled);
    };
  }, []);

  if (installed) return null;

  const onClick = async () => {
    const evt = window.__wzInstallPrompt;
    if (evt) {
      evt.prompt();
      const res = await evt.userChoice.catch(() => null);
      window.__wzInstallPrompt = null;
      if (res?.outcome === "accepted") { setInstalled(true); return; }
      setInstallable(false);
    } else {
      setOpen(true); // no native prompt (iOS / unsupported) → show guide
    }
  };

  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={onClick} data-testid="install-app-button"
        className={`gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50 ${className}`}>
        <Download className="h-4 w-4" />
        {!compact && <span className="text-xs">ثبّت التطبيق</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md font-body" dir="rtl" data-testid="install-guide-dialog">
          <DialogHeader className="text-start">
            <DialogTitle className="font-heading text-xl flex items-center gap-2"><Smartphone className="h-5 w-5 text-blue-600" /> ثبّت تطبيق WorkZone</DialogTitle>
            <DialogDescription className="text-start">أضِف التطبيق لشاشتك الرئيسية لتصلك الإشعارات فورًا وتفتحه بضغطة كتطبيق مستقل.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue={isIOS() ? "ios" : "android"} dir="rtl">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="android" data-testid="install-tab-android" className="gap-1.5"><Smartphone className="h-4 w-4" /> أندرويد</TabsTrigger>
              <TabsTrigger value="ios" data-testid="install-tab-ios" className="gap-1.5"><Apple className="h-4 w-4" /> آيفون (Safari)</TabsTrigger>
            </TabsList>
            <TabsContent value="android" className="space-y-3 pt-4">
              <Step n={1} icon={MoreVertical}>افتح قائمة المتصفح (⋮) أعلى اليمين في Chrome</Step>
              <Step n={2} icon={Plus}>اختر «تثبيت التطبيق» أو «إضافة إلى الشاشة الرئيسية»</Step>
              <Step n={3} icon={Check}>اضغط «تثبيت» — سيظهر أيقونة WorkZone على شاشتك</Step>
              <p className="text-[11px] text-slate-400 pt-1">إن ظهر زر «تثبيت التطبيق» مباشرة فاضغطه بدل هذه الخطوات.</p>
            </TabsContent>
            <TabsContent value="ios" className="space-y-3 pt-4">
              <Step n={1} icon={Share}>افتح الموقع في Safari واضغط زر المشاركة (المربّع مع السهم لأعلى)</Step>
              <Step n={2} icon={Plus}>مرّر واختر «إضافة إلى الشاشة الرئيسية» (Add to Home Screen)</Step>
              <Step n={3} icon={Check}>اضغط «إضافة» — بعدها فعّل الإشعارات من داخل التطبيق</Step>
              <p className="text-[11px] text-amber-700 pt-1">على آيفون تعمل الإشعارات الفورية فقط بعد التثبيت من Safari.</p>
            </TabsContent>
          </Tabs>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="install-guide-close" className="mt-2 gap-1.5"><X className="h-4 w-4" /> إغلاق</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
